import { IDENTITY_REFUSAL_REASONS, IDENTITY_TRIES, identityTriesLeft } from '@dayon/contracts';

/**
 * WHAT REFUSING MEANS FOR THE PLAYER, IN THE REFUSAL DIALOG — the owner, 23 Oct 2026.
 *
 * A refused player may send new photos when the reason is one they can put right and they have had
 * fewer than `IDENTITY_TRIES` refused checks; otherwise they cannot send documents again unless
 * support allows another try ("3, support adds 1": **Allow one more try** on the closed case,
 * OneMoreTry.tsx). The reviewer choosing the reason is the person deciding which, so the dialog says
 * it before the press, by the rule the player's screen is decided by (`identityTriesLeft`), counting
 * the refusal being made as one more.
 */

/** A reason the dialog offers, as the identity routes send it (`refusalReasons`). */
export interface RefusalReason {
  key: string;
  label: string;
  /** Whether the player can put it right themselves. */
  retry: boolean;
}

const ORDINALS = [
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
  'seventh',
  'eighth',
  'ninth',
  'tenth',
];

/** How many more checks the player may send once refused for this reason, this refusal counted. */
function triesLeftAfter(reason: { retry: boolean }, refusalsSoFar: number): number {
  return identityTriesLeft({
    refusals: refusalsSoFar + 1,
    latestRetry: reason.retry,
    grantSinceLatestRefusal: false,
  });
}

/**
 * What the email says once nothing more can be sent: the message every refused player was sent before
 * a refusal could say what to fix. Only if one can be sent: an account with no address, or an address
 * the mailer will not write to, gets none, and nobody knows which before the press.
 */
const CLOSED_EMAIL =
  'If we can write to their address, their email tells them to contact support and does NOT say ' +
  'which reason you chose.';

/**
 * What the player may do next once refused for this reason, and what any email to them says, said to
 * the reviewer. The email follows the same rule: while they may send new photos it carries what to fix
 * — the identity page's own sentence, never the reviewer's note — and otherwise it names no reason.
 */
export function refusalConsequence(reason: { retry: boolean }, refusalsSoFar: number): string {
  const tries = refusalsSoFar + 1;
  if (triesLeftAfter(reason, refusalsSoFar) > 0) {
    return (
      'The player is told what to fix on their identity page — and by email, if we can write to ' +
      `their address — and may send new photos (this will be ${tries} of ${IDENTITY_TRIES} tries).`
    );
  }
  if (reason.retry) {
    return (
      `This is their ${ORDINALS[tries - 1] ?? `${tries}th`} refused check, so they cannot send ` +
      `documents again unless support allows another try. ${CLOSED_EMAIL}`
    );
  }
  return `The player cannot send documents again unless support allows another try. ${CLOSED_EMAIL}`;
}

/** What the verdict route answers, as far as the words after the press read it. */
export interface VerdictAnswer {
  outcome: string;
  playerRef: string;
  /** Whether the refused player may send new photos. */
  maySendAgain?: boolean;
  /** Whether a message to the player was queued. */
  emailed?: boolean;
}

/**
 * WHAT THE REVIEWER IS TOLD ONCE THE VERDICT IS SAVED, from the verdict's own answer: what the player
 * may do now, and whether they were emailed — never "emailed" where no message was queued (no address,
 * or one the mailer will not write to). An answer that does not say reads as closed, and unsent.
 */
export function verdictToast(answer: VerdictAnswer): string {
  const ref = answer.playerRef;
  const emailed = answer.emailed === true;
  if (answer.outcome === 'VERIFIED') {
    return emailed
      ? `${ref} is verified. Their account is open and they have been emailed.`
      : `${ref} is verified. Their account is open. No email could be sent to them.`;
  }
  if (answer.maySendAgain === true) {
    return emailed
      ? `${ref} was refused. They have been emailed what to fix, and may send new photos.`
      : `${ref} was refused. They may send new photos, and their identity page says what to fix; ` +
          'no email could be sent to them.';
  }
  return emailed
    ? `${ref} was refused. They have been emailed and told to contact support.`
    : `${ref} was refused. No email could be sent to them; their identity page tells them to ` +
        'contact support.';
}

/** The two groups of the reason list, by what the reason would let the player do. */
export const MAY_SEND_AGAIN = 'The player may send new photos';
export const CANNOT_SEND_AGAIN = 'The player cannot send documents again unless support allows it';

/**
 * THE REASON LIST, GROUPED by what each reason would let the player do — the reasons that let them
 * send new photos, then those that end it — each group in the order the routes send. On what would
 * be the player's last refused check every reason ends it, and the list is one group.
 */
export function reasonGroups<R extends { retry: boolean }>(
  reasons: readonly R[],
  refusalsSoFar: number,
): { label: string; reasons: R[] }[] {
  const may = reasons.filter((r) => triesLeftAfter(r, refusalsSoFar) > 0);
  const cannot = reasons.filter((r) => triesLeftAfter(r, refusalsSoFar) === 0);
  return [
    ...(may.length > 0 ? [{ label: MAY_SEND_AGAIN, reasons: may }] : []),
    ...(cannot.length > 0 ? [{ label: CANNOT_SEND_AGAIN, reasons: cannot }] : []),
  ];
}

/**
 * THE HINT UNDER THE REASON. Why the list is closed, and then the choice most worth a word: "The
 * face does not match the document" is a reason the player can put right and "The person is not the
 * document holder" is not, and the second is for somebody else's ID. In the reason's own words.
 */
const NOT_THE_HOLDER =
  IDENTITY_REFUSAL_REASONS.find((r) => r.key === 'NOT_THE_HOLDER')?.label ??
  'The person is not the document holder';
export const REASON_HINT =
  'A closed set, so refusals can be counted and compared across reviewers. If you think the ' +
  `person is someone else, choose “${NOT_THE_HOLDER}”.`;

/**
 * THE WORDS OF THE DANGER NOTICE AT THE TOP OF THE DIALOG (the notice itself is the dialog's own):
 * what refusing costs, and — once a reason is chosen — what the player may do next and what any
 * email to them says. Spoken as it changes, for a reviewer who cannot see it change.
 */
export function RefusalNotice({
  reason,
  refusalsSoFar,
}: {
  reason: { retry: boolean } | null;
  refusalsSoFar: number;
}) {
  return (
    <div>
      The account stays closed for deposits, play and withdrawals. Refusing cannot be undone.{' '}
      <span aria-live="polite">
        {reason === null
          ? 'What the player may do next, and what any email to them says, depends on the reason you choose.'
          : refusalConsequence(reason, refusalsSoFar)}
      </span>
    </div>
  );
}
